/**
 * Lecturer Availability Service
 * Handles API operations for lecturer availability filters
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { LECTURER_AVAILABILITY_ENDPOINTS, SIS_SCHEMA_ENDPOINTS } from '../api';
import { SchoolLocationRead, ModuleVariantRead, SchoolRead } from '../models';

/**
 * Nested structures for structured availability filtering
 */
export interface ModuleVariantWithSlots extends ModuleVariantRead {
  slots: any[];
}

export interface LocationWithVariants extends SchoolLocationRead {
  variants: ModuleVariantWithSlots[];
}

export interface PlanningQuarter {
  year: number;
  quarter: number;
  start_date: string;
  end_date: string;
}

export interface PlannedBlock {
  lecture_block_id: number;
  planning_status: string;
  date: string;
  begin_time: string;
  end_time: string;
  module: string;
  location: string;
  school_id: number;
  location_id: number;
  variant_id: number;
  is_guaranteed: boolean;
}

export interface SchoolWithNestedDetails extends SchoolRead {
  locations: LocationWithVariants[];
  planning_quarters: PlanningQuarter[];
}

/**
 * Response interface for lecturer availability filters
 */
export interface LecturerAvailabilityFilters {
  schools: SchoolWithNestedDetails[];
  earliest_start_date: string | null; // ISO date string from database
  saved_preferences?: {
    [schoolId: number]: {
      selected_location_ids: number[];
      selected_variant_ids: number[];
    };
  };
}

/**
 * Response interface for lecturer validation
 */
export interface LecturerValidation {
  is_lecturer: boolean;
  teachability_count: number;
  modules: string[];
}

/**
 * Request interface for saving daily availability
 * 3-state system:
 * - 'normal' (gray): No DB entry - deletes entry if exists
 * - 'available' (green): DB entry with availability_date set
 * - 'unavailable' (red): DB entry with availability_date NULL
 */
export interface DailyAvailabilityRequest {
  school_id: number;
  location_id: number;
  variant_id: number;
  slot_id?: number; // Optional: specific slot ID for slot-level availability
  availability_date: string; // ISO date string (YYYY-MM-DD)
  status: 'normal' | 'available' | 'unavailable';
}

/**
 * Response interface for daily availability
 */
export interface DailyAvailabilityResponse {
  success: boolean;
  contact_id: number;
  school_id: number;
  start_date: string;
  end_date: string;
  total_available: number;
  total_unavailable: number;
  availability_by_date: {
    [date: string]: Array<{
      location_id: number;
      location_shortname: string;
      location_name: string;
      variant_id: number;
      variant_code: string;
      variant_name: string;
      slot_id?: number; // Can be undefined for variant-level availability
      status: string; // 'available' or 'unavailable'
    }>;
  };
  available_dates: string[];
  unavailable_dates: string[];
}

@Injectable({
  providedIn: 'root',
})
export class LecturerAvailabilityService {
  private readonly http = inject(HttpClient);

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Get availability filters for a specific lecturer based on degree_run_postings
   * Returns schools, locations, and variants relevant to the lecturer
   * based on their teachabilities and degree_run_postings
   *
   * @param contactId The lecturer's contact ID
   */
  getLecturerAvailabilityFiltersDegreePostings(
    contactId: number,
    schoolId: number | null = null,
    monthsAhead: number = 9,
  ): Observable<LecturerAvailabilityFilters> {
    this.loading.set(true);
    this.error.set(null);

    const params: any = {};
    if (monthsAhead !== 9) {
      params.months_ahead = monthsAhead;
    }
    if (schoolId) {
      params.fk_school_id = schoolId;
    }

    return this.http
      .get<LecturerAvailabilityFilters>(
        LECTURER_AVAILABILITY_ENDPOINTS.filtersDegreePostings(contactId),
        {
          params,
          headers: this.createHeaders(),
        },
      )
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get module variants available for a specific location and lecturer
   * Returns only variants that are available at the selected location
   * based on degree_run_postings
   *
   * @param contactId The lecturer's contact ID
   * @param locationId The school location ID to filter by
   */
  getVariantsByLocation(contactId: number, locationId: number): Observable<ModuleVariantRead[]> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<ModuleVariantRead[]>(
        LECTURER_AVAILABILITY_ENDPOINTS.variantsByLocation(contactId, locationId),
        {
          headers: this.createHeaders(),
        },
      )
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get availability filters for a specific lecturer
   * Returns schools, locations, and variants relevant to the lecturer
   * based on their teachabilities and upcoming module_run_templates
   *
   * @param contactId The lecturer's contact ID
   * @param monthsAhead Number of months ahead from next quarter start (default: 9)
   */
  getLecturerAvailabilityFilters(
    contactId: number,
    monthsAhead: number = 9,
  ): Observable<LecturerAvailabilityFilters> {
    this.loading.set(true);
    this.error.set(null);

    const params: any = {};
    if (monthsAhead !== 9) {
      params.months_ahead = monthsAhead;
    }

    return this.http
      .get<LecturerAvailabilityFilters>(LECTURER_AVAILABILITY_ENDPOINTS.filters(contactId), {
        params,
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Validate if a contact is a lecturer
   * Checks if the contact has any teachabilities
   *
   * @param contactId The contact ID to validate
   */
  validateLecturer(contactId: number): Observable<LecturerValidation> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<LecturerValidation>(LECTURER_AVAILABILITY_ENDPOINTS.validate(contactId), {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Save lecturer preferences for locations and module variants
   * Uses Strategie A: Combined entries (Cartesian product of locations × variants)
   *
   * @param contactId The lecturer's contact ID
   * @param schoolId The school ID
   * @param locationIds Array of selected location IDs (in order)
   * @param variantIds Array of selected variant IDs (in order)
   */
  savePreferences(
    contactId: number,
    schoolId: number,
    locationIds: number[],
    variantIds: number[],
  ): Observable<any> {
    this.loading.set(true);
    this.error.set(null);

    const preferences = {
      school_id: schoolId,
      location_ids: locationIds,
      variant_ids: variantIds,
    };

    return this.http
      .post(LECTURER_AVAILABILITY_ENDPOINTS.savePreferences(contactId), preferences, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Save daily availability for a specific date
   * Green (available) = Insert record with date
   * Red (not available) = Delete record for that date
   *
   * @param contactId The lecturer's contact ID
   * @param request Contains school_id, location_id, variant_id, date, and availability status
   */
  saveDailyAvailability(contactId: number, request: DailyAvailabilityRequest): Observable<any> {
    return this.http
      .post(LECTURER_AVAILABILITY_ENDPOINTS.saveDaily(contactId), request, {
        headers: this.createHeaders(),
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Get daily availability for a lecturer within a date range
   * Returns only dates where the lecturer is AVAILABLE (green)
   *
   * @param contactId The lecturer's contact ID
   * @param schoolId School ID to filter by
   * @param startDate Start date of the range (YYYY-MM-DD)
   * @param endDate End date of the range (YYYY-MM-DD)
   */
  getDailyAvailability(
    contactId: number,
    schoolId: number,
    startDate: string,
    endDate: string,
  ): Observable<DailyAvailabilityResponse> {
    const params = {
      school_id: schoolId.toString(),
      start_date: startDate,
      end_date: endDate,
    };

    return this.http
      .get<DailyAvailabilityResponse>(LECTURER_AVAILABILITY_ENDPOINTS.getDaily(contactId), {
        params,
        headers: this.createHeaders(),
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Create authorization headers
   */
  private createHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    });
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getPlannedBlocks(contactId: number): Observable<PlannedBlock[]> {
    return this.http.get<PlannedBlock[]>(SIS_SCHEMA_ENDPOINTS.planningAutomation.lecturerBlocks(contactId));
  }

  lecturerAction(blockId: number, action: 'APPROVE' | 'DECLINE'): Observable<any> {
    return this.http.post(SIS_SCHEMA_ENDPOINTS.planningAutomation.lecturerAction, {
      lecture_block_id: blockId,
      action
    });
  }

  /**
   * Error handler
   */
  private handleError(error: any): Observable<never> {
    this.loading.set(false);

    let errorMessage = 'An unexpected error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server Error: ${error.status} - ${error.message}`;
      if (error.error?.detail) {
        errorMessage = error.error.detail;
      }
    }

    this.error.set(errorMessage);
    console.error('LecturerAvailabilityService Error:', errorMessage, error);

    return throwError(() => new Error(errorMessage));
  }
}
